<template>
  <div class="monaco-editor-wrapper" :class="{ 'fullscreen': isFullscreen }" :style="editorWrapperStyle">
    <div class="editor-header" v-if="showFullscreenButton" :style="isFullscreen ? { margin: '0', padding: '0 10px' } : {}">
      <div class="editor-title" v-if="isFullscreen && title">
        {{ title }}
      </div>
      <div class="editor-actions">
        <button 
          class="fullscreen-btn" 
          @click="toggleFullscreen"
          :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
        >
          <svg v-if="!isFullscreen" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
          </svg>
          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
          </svg>
        </button>
      </div>
    </div>
    <div ref="editorContainer" :style="{ height: editorHeight, width: '100%', margin: '0', padding: '0', border: 'none', outline: 'none' }"></div>
  </div>
</template>

<script>
import loader from '@monaco-editor/loader'
import { functionScopeToDts, platformFunctionsToDts, componentIDLsToDts, generateConcreteTypes } from '../../../utils/idlToDts'

export default {
  name: 'MonacoEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: 'javascript'
    },
    height: {
      type: Number,
      default: 300
    },
    options: {
      type: Object,
      default: () => ({})
    },
    showFullscreenButton: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: ''
    },
    enableProtokolTypes: {
      type: Boolean,
      default: false
    },
    // Optional: PlatformFunctionSignature object for the function expression scope.
    // When set, injects typed `declare const input: { body: <T>; ... }` into the editor.
    functionSignature: {
      type: Object,
      default: null
    },
    // Optional: raw DTS string to inject as scope declarations.
    // When provided, takes precedence over functionSignature.
    // Callers can use componentFunctionScopeToDts() or any custom DTS.
    scopeDeclarations: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      editor: null,
      isInitialized: false,
      monaco: null,
      isFullscreen: false
    }
  },
  computed: {
    editorWrapperStyle() {
      if (this.isFullscreen) {
        return {
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          zIndex: '9999',
          backgroundColor: 'var(--bg-main-end)',
          height: '100vh',
          width: '100vw',
          margin: '0',
          padding: '0'
        }
      }
      return { height: this.height + 'px' }
    },
    editorHeight() {
      if (this.isFullscreen) {
        return this.showFullscreenButton ? 'calc(100vh - 40px)' : '100vh'
      }
      return this.showFullscreenButton ? 'calc(100% - 40px)' : '100%'
    }
  },
  watch: {
    value(newValue) {
      if (this.editor && this.isInitialized) {
        const currentValue = this.editor.getValue()
        if (currentValue !== newValue) {
          this.editor.setValue(newValue || '')
        }
      }
    },
    language(newLanguage) {
      if (this.editor && this.isInitialized && this.monaco) {
        this.monaco.editor.setModelLanguage(this.editor.getModel(), newLanguage)
      }
    },
    options: {
      deep: true,
      handler(newOptions) {
        if (this.editor && this.isInitialized) {
          this.editor.updateOptions(newOptions)
        }
      }
    },
    functionSignature(newSig) {
      if (this.isInitialized && !this.scopeDeclarations) {
        this.injectFunctionScope(newSig)
      }
    },
    scopeDeclarations(newDts) {
      if (this.isInitialized) {
        this.injectScopeDeclarations(newDts)
      }
    }
  },
  methods: {
    async initializeEditor() {
      if (!this.$refs.editorContainer) return

      // Load Monaco Editor using the loader
      this.monaco = await loader.init()

      // Set up compiler options and diagnostics
      this.setupTypeDefinitions()

      // Load Protokol types BEFORE creating the editor so the TS worker has
      // all declarations (ComponentModels, PlatformFunctions, etc.) from the
      // very first analysis pass. If loaded after, the worker caches unresolved
      // generic parameters (C stays string instead of narrowing to "test").
      if (this.enableProtokolTypes) {
        await this.loadProtokolTypes()
      }

      // Default Monaco Editor options
      const defaultOptions = {
        theme: 'vs-dark',
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        fontSize: 14,
        lineNumbers: 'on',
        folding: true,
        wordWrap: 'on',
        tabSize: 2,
        insertSpaces: true,
        detectIndentation: false,
        selectOnLineNumbers: true,
        roundedSelection: false,
        scrollbar: {
          vertical: 'auto',
          horizontal: 'auto'
        }
      }

      // Merge with user-provided options (options prop is for Monaco configuration only)
      const editorOptions = { 
        ...defaultOptions, 
        ...this.options,
        // These are controlled by component props, not options
        value: this.value || '',
        // When Protokol types are enabled, use TypeScript mode so string literals
        // stay narrowed (e.g. "send_email" stays "send_email", not string).
        // This is required for fn.run() generic overloads to resolve correctly.
        // Plain JS is valid TypeScript — user code is unaffected.
        language: this.enableProtokolTypes ? 'typescript' : this.language
      }

      // When Protokol types are enabled, use a .ts URI so the TypeScript language
      // service does full generic inference (e.g. new Component("test") narrows C = "test").
      // Without a .ts URI, Monaco's TS worker may skip generic constructor inference.
      if (this.enableProtokolTypes) {
        const uid = Date.now() + Math.random().toString(36).slice(2)
        const uri = this.monaco.Uri.parse(`file:///ptkl-script-${uid}.ts`)
        const model = this.monaco.editor.createModel(
          editorOptions.value || '',
          'typescript',
          uri
        )
        this._ptklModel = model
        delete editorOptions.value
        editorOptions.model = model
      }

      // Create the editor
      this.editor = this.monaco.editor.create(this.$refs.editorContainer, editorOptions)

      // Set up event listeners
      this.editor.onDidChangeModelContent(() => {
        const value = this.editor.getValue()
        this.$emit('input', value)
        this.$emit('change', value)
      })

      this.editor.onDidFocusEditorWidget(() => {
        this.$emit('focus')
      })

      this.editor.onDidBlurEditorWidget(() => {
        this.$emit('blur')
      })

      this.isInitialized = true
      this.$emit('ready', this.editor)

    },

    getValue() {
      return this.editor ? this.editor.getValue() : ''
    },

    setValue(value) {
      if (this.editor) {
        this.editor.setValue(value || '')
      }
    },

    focus() {
      if (this.editor) {
        this.editor.focus()
      }
    },

    blur() {
      if (this.editor) {
        this.editor.trigger('keyboard', 'editor.action.blur', {})
      }
    },

    getEditor() {
      return this.editor
    },

    setupTypeDefinitions() {
      if (!this.monaco) return

      // Enable more aggressive IntelliSense
      this.monaco.languages.typescript.javascriptDefaults.setEagerModelSync(true)
      this.monaco.languages.typescript.typescriptDefaults.setEagerModelSync(true)

      // Suppress top-level 'await' errors (1375, 1378) and top-level 'return'
      // error (1108). Our eval server wraps user code in an async function,
      // so top-level await and return are valid at runtime even though the
      // file has no import/export.
      const diagnosticsOptions = { diagnosticCodesToIgnore: [1108, 1375, 1378] }
      this.monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(diagnosticsOptions)
      this.monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(diagnosticsOptions)

      // Configure TypeScript compiler options for better IntelliSense.
      // target ES2022 aligns with the eval server's esbuild transpiler (target: 'es2022').
      // Do NOT specify 'lib' explicitly — programmatic setCompilerOptions does not expand
      // shorthand lib names (e.g. 'ES2020') into the full lib chain the way tsconfig does,
      // which causes "Cannot find name 'Promise'" / "Cannot find name 'Array'" errors.
      // Omitting 'lib' lets TypeScript derive the correct default libs from 'target'.
      this.monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: this.monaco.languages.typescript.ScriptTarget.ES2022,
        allowNonTsExtensions: true,
        moduleResolution: this.monaco.languages.typescript.ModuleResolutionKind.NodeJs,
        module: this.monaco.languages.typescript.ModuleKind.CommonJS,
        noEmit: true,
        esModuleInterop: true,
        allowJs: true,
        checkJs: true,
        typeRoots: [],
        skipLibCheck: true
      })

      this.monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
        target: this.monaco.languages.typescript.ScriptTarget.ES2022,
        allowNonTsExtensions: true,
        moduleResolution: this.monaco.languages.typescript.ModuleResolutionKind.NodeJs,
        module: this.monaco.languages.typescript.ModuleKind.ESNext,
        noEmit: true,
        esModuleInterop: true,
        strict: true,
        allowJs: true,
        checkJs: true,
        typeRoots: [],
        skipLibCheck: true
      })

      // Protokol types are loaded in initializeEditor() before editor creation
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      
      // Trigger layout update after DOM changes
      this.$nextTick(() => {
        if (this.editor) {
          this.editor.layout()
        }
      })
      
      this.$emit('fullscreen-change', this.isFullscreen)
    },

    handleKeydown(event) {
      if (event.key === 'Escape' && this.isFullscreen) {
        this.toggleFullscreen()
      }
    },

    // Injects "declare const input: { body: <T>; ... }" for platform function expression editors.
    // Called after loadProtokolTypes and whenever functionSignature prop changes.
    injectFunctionScope(signature) {
      if (!this.monaco) return
      const dts = functionScopeToDts(signature)
      const path = 'file:///ptkl-idl/function-scope.d.ts'
      this.monaco.languages.typescript.javascriptDefaults.addExtraLib(dts, path)
      this.monaco.languages.typescript.typescriptDefaults.addExtraLib(dts, path)
    },

    // Injects a raw DTS string as scope declarations (e.g. "declare const input: any;").
    // Used by callers that need custom scope (component functions, workflows, etc.).
    injectScopeDeclarations(dts) {
      if (!this.monaco || !dts) return
      const path = 'file:///ptkl-idl/function-scope.d.ts'
      this.monaco.languages.typescript.javascriptDefaults.addExtraLib(dts, path)
      this.monaco.languages.typescript.typescriptDefaults.addExtraLib(dts, path)
    },

    async loadProtokolTypes() {
      try {
        // 1. Load the base SDK types (empty PlatformFunctions / ComponentModels / ComponentFunctions stubs)
        const response = await fetch('/types/monaco.d.ts')
        if (!response.ok) {
          console.warn('[Monaco] Protokol types file not found at /types/monaco.d.ts')
          return
        }
        let typeDefinitions = await response.text()

        // 2. Fetch IDLs and patch the base types string IN-PLACE.
        //    Monaco doesn't reliably merge interface declarations across separate
        //    addExtraLib files, so we replace the empty stubs before loading.
        try {
          const { data } = await this.$platform.idl()

          // 2a. Platform function signatures → replace empty PlatformFunctions
          const fnDts = platformFunctionsToDts(
            Object.entries(data.functions || {}).map(([name, signature]) => ({ name, signature }))
          )
          if (fnDts) {
            typeDefinitions = typeDefinitions.replace(
              /declare\s+interface\s+PlatformFunctions\s*\{[^}]*\}/g,
              'declare ' + fnDts.trim()
            )
          }

          // 2b. Component + extension IDLs → replace empty ComponentModels & ComponentFunctions
          const { modelsDts, functionsDts, typesDts } = componentIDLsToDts(data.components || {})
          if (typesDts) {
            // Prepend named type declarations so they are available in field/function types.
            typeDefinitions = typesDts + '\n\n' + typeDefinitions
          }
          if (modelsDts || functionsDts) {
            if (modelsDts) {
              typeDefinitions = typeDefinitions.replace(
                /declare\s+interface\s+ComponentModels\s*\{[^}]*\}/g,
                modelsDts
              )
            }
            if (functionsDts) {
              typeDefinitions = typeDefinitions.replace(
                /declare\s+interface\s+ComponentFunctions\s*\{[^}]*\}/g,
                functionsDts
              )
            }
          }

          // 2c. Generate concrete per-ref interfaces and constructor overloads.
          //     Monaco's quickinfo does not resolve generic indexed-access or
          //     conditional return types, so we generate per-ref interfaces with
          //     inline concrete types and patch constructor/method signatures.
          const concreteTypes = generateConcreteTypes(
            data.components || {},
            Object.entries(data.functions || {}).map(([name, signature]) => ({ name, signature }))
          )

          // Append per-ref interface declarations to the type definitions
          if (concreteTypes.interfacesDts) {
            typeDefinitions += '\n\n' + concreteTypes.interfacesDts
          }

          // Patch ProtokolSDK.Component with concrete constructor overloads
          if (concreteTypes.ctorOverloads) {
            typeDefinitions = typeDefinitions.replace(
              'Component: { new<C extends string>(ref: C, options?: ComponentOptions): ComponentConstructor<C> };',
              'Component: {\n' + concreteTypes.ctorOverloads + '\n    new<C extends string>(ref: C, options?: ComponentOptions): ComponentConstructor<C>;\n  };'
            )
          }

          // Patch PlatformConstructor.component() with concrete overloads
          if (concreteTypes.platformOverloads) {
            typeDefinitions = typeDefinitions.replace(
              '  component<C extends string>(ref: C, options?: ComponentOptions): ComponentConstructor<C>;',
              concreteTypes.platformOverloads + '\n  component<C extends string>(ref: C, options?: ComponentOptions): ComponentConstructor<C>;'
            )
          }

          // Patch FunctionsConstructor.run() with concrete overloads
          if (concreteTypes.runOverloads) {
            typeDefinitions = typeDefinitions.replace(
              "  // Typed overload: input/output inferred from the function's IDL signature.",
              concreteTypes.runOverloads + "\n  // Typed overload: input/output inferred from the function's IDL signature."
            )
          }
        } catch (e) {
          console.warn('[Monaco] Could not fetch platform IDLs:', e)
        }

        // 3. Load everything as a single file — all interfaces are populated.
        // Strip the hardcoded `declare const input` from the SDK types so the
        // per-context declaration injected in step 4 isn't shadowed by it.
        typeDefinitions = typeDefinitions.replace(
          /^declare const input:.*$/m,
          '// input type is injected per-context by the editor (see step 4)'
        )
        const mainPath = 'file:///node_modules/@types/protokol-sdk/index.d.ts'
        this.monaco.languages.typescript.javascriptDefaults.addExtraLib(typeDefinitions, mainPath)
        this.monaco.languages.typescript.typescriptDefaults.addExtraLib(typeDefinitions, mainPath)

        // 4. Inject per-function scope types (declare const input).
        // For platform function editors: use functionSignature → functionScopeToDts.
        // For component function editors: use scopeDeclarations (always provided).
        // Fallback: inject `declare const input: any` so we NEVER emit the
        // platform HTTP-envelope type (body/query/headers) in a context that
        // doesn't expect it.
        if (this.scopeDeclarations) {
          this.injectScopeDeclarations(this.scopeDeclarations)
        } else if (this.functionSignature !== null && this.functionSignature !== undefined) {
          this.injectFunctionScope(this.functionSignature)
        } else {
          // No scope provided at all — inject a safe any fallback instead of
          // the platform HTTP-envelope type.
          this.injectScopeDeclarations('declare const input: any;\n')
        }

      } catch (error) {
        console.error('[Monaco] Failed to load Protokol types:', error)
      }
    }
  },

  mounted() {
    this.initializeEditor()
    
    // Add global keydown listener for ESC key
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    if (this.editor) {
      this.editor.dispose()
    }
    if (this._ptklModel) {
      this._ptklModel.dispose()
      this._ptklModel = null
    }
    
    // Remove global keydown listener
    document.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>

<style scoped>
.monaco-editor-wrapper {
  border: none;
  border-radius: 0;
  overflow: hidden;
  position: relative;
}

.monaco-editor-wrapper.fullscreen {
  border: none !important;
  border-radius: 0 !important;
  overflow: visible;
  margin: 0 !important;
  padding: 0 !important;
  /* Remove conflicting position styles - handled by inline styles */
}

/* Aggressive reset for fullscreen mode */
.monaco-editor-wrapper.fullscreen,
.monaco-editor-wrapper.fullscreen * {
  box-sizing: border-box !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* Restore necessary padding only where needed */
.monaco-editor-wrapper.fullscreen .editor-header {
  padding: 0 10px !important;
  margin: 0 !important;
}

.monaco-editor-wrapper.fullscreen .fullscreen-btn {
  padding: 6px !important;
  margin: 0 !important;
}

.monaco-editor-wrapper.fullscreen > div {
  margin: 0 !important;
}

/* Target Monaco Editor specifically */
.monaco-editor-wrapper.fullscreen .monaco-editor,
.monaco-editor-wrapper.fullscreen .monaco-editor .monaco-editor-background,
.monaco-editor-wrapper.fullscreen .monaco-editor .decorationsOverviewRuler {
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
}

/* Override any Bootstrap or global container styles */
.monaco-editor-wrapper.fullscreen .container,
.monaco-editor-wrapper.fullscreen .container-fluid,
.monaco-editor-wrapper.fullscreen .row,
.monaco-editor-wrapper.fullscreen .col,
.monaco-editor-wrapper.fullscreen [class*="col-"] {
  margin: 0 !important;
  padding: 0 !important;
}

.editor-header {
  background: var(--bg-card);
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 10px;
  border-bottom: 1px solid #3e3e42;
  flex-shrink: 0; /* Prevent header from shrinking */
}

.editor-title {
  color: #cccccc;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
  margin-right: auto; /* This pushes the title to the left and actions to the right */
}

.editor-actions {
  display: flex;
  gap: 8px;
}

.fullscreen-btn {
  background: transparent;
  border: none;
  color: #cccccc;
  padding: 6px;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
}

.fullscreen-btn:hover {
  background: var(--bg-card-secondary);
  color: var(--text-primary);
}

.fullscreen-btn:focus {
  outline: 1px solid #007acc;
  outline-offset: 2px;
}
</style>